import { FC } from "react";

import { useFormContext, useWatch } from "react-hook-form";

import { useRateScheduleDigest } from "../../../RatesDigest/RatesDigestContext";
import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";

import { resolveEventEndDate, resolveScheduleDates } from "../../../helpers/resolveScheduleDates";

import { buildRateStatusChip } from "./buildRateStatusChip";
import { getVariantItemCapacity } from "./getVariantItemCapacity";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import { Check } from "@/icons/filled";
import { Dot } from "@/icons/outlined";

const DOT_VIBES = {
  positive: "text-positive-quiet",
  warning: "text-warning-quiet",
  danger: "text-danger-quiet",
} as const;

export const RateStatusChip: FC = () => {
  const { t } = useTranslate();
  const { alreadyBoughtTicketsCount, formCategory, recurrences, hasMultipleDates } = useRateContext();
  const { control } = useFormContext<StepTicketsFormData>();

  const seats = useWatch({ control, name: useRateFieldName("seats") });
  const shopVariants = useWatch({ control, name: useRateFieldName("shopVariants") });
  const variantSeats = useWatch({ control, name: useRateFieldName("variantSeats") });
  const isGroup = useWatch({ control, name: useRateFieldName("isGroup") });
  const attendeesByTicket = useWatch({ control, name: useRateFieldName("attendeesByTicket") });
  const isScheduled = useWatch({ control, name: useRateFieldName("isScheduled") });
  const salesStartMode = useWatch({ control, name: useRateFieldName("salesStartMode") });
  const salesStartDate = useWatch({ control, name: useRateFieldName("salesStartDate") });
  const salesStartAfterRateId = useWatch({ control, name: useRateFieldName("salesStartAfterRateId") });
  const salesEndMode = useWatch({ control, name: useRateFieldName("salesEndMode") });
  const salesEndDate = useWatch({ control, name: useRateFieldName("salesEndDate") });
  const allRates = useRateScheduleDigest();
  const resolvedSchedule = resolveScheduleDates(
    { isScheduled, salesStartMode, salesStartDate, salesStartAfterRateId, salesEndMode, salesEndDate },
    allRates
  );
  const defaultEndDate = isScheduled && salesEndMode === "formDefault" ? resolveEventEndDate(recurrences) : null;

  const variantItemCapacity = getVariantItemCapacity({
    shopVariants,
    variantSeats,
    soldCount: alreadyBoughtTicketsCount,
  });

  const chip = buildRateStatusChip({
    soldCount: alreadyBoughtTicketsCount,
    seats: variantItemCapacity === undefined ? seats : variantItemCapacity,
    isGroup,
    formCategory,
    attendeesByTicket,
    hasMultipleDates,
    scheduleStartDate: resolvedSchedule.startDate,
    scheduleEndDate: resolvedSchedule.endDate ?? defaultEndDate,
    now: new Date(),
  });

  return (
    <DashboardChip
      shape="rounded"
      vibe={chip.vibe}
      icon={chip.withCheckIcon ? <Check /> : <Dot />}
      slotProps={{
        icon: chip.withCheckIcon
          ? { vibe: "text-positive-quiet" }
          : {
              vibe: DOT_VIBES[chip.dotVibe],
              sx: { width: 12, height: 12, "& > svg": { transform: "scale(2)" } },
            },
      }}
      label={t("dashboard", chip.key, chip.params)}
      data-test="rate-status-chip"
    />
  );
};
